import { useEffect, useRef, useState } from "react";
import { MonthlyPrice } from "../lib/pricing";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Flame, CheckCircle2, XCircle, Lock, ArrowRight, ArrowLeft, BookOpen, Layers,
  Timer, Bookmark, BookmarkCheck, RotateCcw, Eye, Award, ChevronRight, ChevronDown, Target, Zap, Pause, Play, Mic, BarChart3, Building2, Shuffle,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { StreakCard } from "../components/StreakCard";
import { RoadMap } from "../components/RoadMap";
import { ReportQuestion } from "../components/ReportQuestion";
import { DashboardHub, DashboardExtras } from "../components/dashboard/DashboardHub";
import { PracticeProgress, BoardProgress, useDashboardData } from "../components/dashboard/ProgressPanels";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent } from "../components/ui/dialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MODES = [
  { id: "practice", label: "Practice", icon: BookOpen, desc: "Answer questions one by one with instant explanations." },
  { id: "flashcards", label: "Flashcards", icon: Layers, desc: "Question + A–D on the front; flip for the answer and a short rationale." },
  { id: "exam", label: "Timed Exam", icon: Timer, desc: "A scored, timed mock exam. Pass mark is 70%." },
];

function BookmarkBtn({ id, bookmarks, onToggle }) {
  const marked = bookmarks.includes(id);
  return (
    <button onClick={() => onToggle(id)} data-testid={`bookmark-toggle-${id}`}
      className="text-slate-400 hover:text-amber-500 transition-colors">
      {marked ? <BookmarkCheck className="w-5 h-5 text-amber-500" /> : <Bookmark className="w-5 h-5" />}
    </button>
  );
}

export default function Study({ view = "study" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ tracks: [], categories: [] });
  const [showDetails, setShowDetails] = useState(false);
  const [mode, setMode] = useState("practice");
  const [track, setTrack] = useState(null);
  const [stage, setStage] = useState("setup"); // setup | configure | active | exam-result
  const [category, setCategory] = useState([]); // selected sections (empty = all)
  const [setSize, setSetSize] = useState(25);
  const [examTitle, setExamTitle] = useState("");
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ correct: 0, answered: 0 });
  const [flipped, setFlipped] = useState(false);
  const nextRef = useRef(() => {}); const prevRef = useRef(() => {});
  useEffect(() => {
    if (mode !== "flashcards" || stage !== "active" || !questions.length) return undefined;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(e.target.tagName) || e.target.isContentEditable) return;
      if (e.code === "Space") { e.preventDefault(); setFlipped((f) => !f); }
      else if (e.code === "ArrowRight") { e.preventDefault(); nextRef.current(); }
      else if (e.code === "ArrowLeft") { e.preventDefault(); prevRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, stage, questions.length]);
  const [bookmarks, setBookmarks] = useState([]);
  const [examAnswers, setExamAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [examReport, setExamReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weakLoading, setWeakLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [allowance, setAllowance] = useState(null); // Free only: { study: {track: {limit, used, remaining}}, flashcards: {limit, used, remaining}, resets_at }
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitKind, setLimitKind] = useState("daily"); // daily | flashcards
  const seenCards = useRef(new Set());
  const loadAllowance = () => api.get("/study/allowance").then(({ data }) => setAllowance(data.is_premium ? null : data)).catch(() => {});
  const isFree = !user?.is_premium && !!allowance;
  const leftFor = (t) => (isFree ? (allowance.study?.[t]?.remaining ?? 0) : Infinity);
  const flashLeft = leftFor(track); // flashcards share the track's daily pool with practice and timed exams
  const openLimit = (kind) => { setLimitKind(kind); setLimitOpen(true); loadAllowance(); };
  useEffect(() => {
    // Free flashcards: each unique card shown today costs one item from the track's shared daily pool — counted server-side once per card per day.
    if (mode !== "flashcards" || stage !== "active" || !isFree) return;
    const card = questions[idx];
    if (!card || seenCards.current.has(card.id)) return;
    seenCards.current.add(card.id);
    api.post("/study/flashcard-seen", { question_id: card.id })
      .then(({ data }) => { if (!data.is_premium && data.track) setAllowance((a) => (a ? { ...a, study: { ...a.study, [data.track]: data } } : a)); })
      .catch((e) => { if (e.response?.data?.detail?.code === "daily_limit_reached") openLimit("daily"); });
  }, [mode, stage, idx, questions, isFree]); // eslint-disable-line react-hooks/exhaustive-deps
  const [level, setLevel] = useState(null);
  const [retry, setRetry] = useState(null); // { wrong: [], round, total }
  const [missed, setMissed] = useState({ count: 0, by_track: {} });
  const [savedExams, setSavedExams] = useState([]);
  const dash = useDashboardData();
  const [examFormat, setExamFormat] = useState("custom");
  const [totalSeconds, setTotalSeconds] = useState(0);
  const examRef = useRef({});
  examRef.current = { stage, mode, track, questions, examAnswers, idx, timeLeft, examTitle, examFormat, totalSeconds, examReport };

  const loadSaved = () => api.get("/exam/saved").then(({ data }) => setSavedExams(data.sessions || [])).catch(() => {});
  useEffect(() => { if (user) loadSaved(); }, [user, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePayload = () => {
    const s = examRef.current;
    return { track: s.track, title: s.examTitle, format: s.examFormat, question_ids: s.questions.map((q) => q.id),
      answers: s.examAnswers, idx: s.idx, time_left_seconds: s.timeLeft, total_seconds: s.totalSeconds };
  };
  const examInProgress = () => { const s = examRef.current; return s.stage === "active" && s.mode === "exam" && s.questions.length > 0 && !s.examReport; };

  // Auto-save an in-progress exam every 20s, on unmount, and when the tab is closed (keepalive fetch).
  useEffect(() => {
    if (!(stage === "active" && mode === "exam")) return;
    const tick = setInterval(() => { if (examInProgress()) api.post("/exam/save", savePayload()).catch(() => {}); }, 20000);
    const onUnload = () => {
      if (!examInProgress()) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exam/save`, { method: "POST", keepalive: true, credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(savePayload()) }).catch(() => {});
    };
    window.addEventListener("beforeunload", onUnload);
    return () => { clearInterval(tick); window.removeEventListener("beforeunload", onUnload); onUnload(); };
  }, [stage, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dashboard and Study share this component, so a top-nav click used to leave an active set/exam on screen.
  // Any navigation (including tapping "Study" again) exits the current activity; an in-progress exam is saved first.
  const location = useLocation();
  const navKey = useRef(location.key);
  useEffect(() => {
    if (navKey.current === location.key) return;
    navKey.current = location.key;
    if (stage === "setup") return;
    if (examInProgress()) { api.post("/exam/save", savePayload()).then(() => loadSaved()).catch(() => {}); toast.info("Exam saved — resume it anytime from the Study page."); }
    setQuestions([]); setExamReport(null); setRetry(null); setStarredOnly(false); setFullDeck(null); setStage("setup");
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const pauseExam = async () => {
    try {
      await api.post("/exam/save", savePayload());
      toast.success("Exam paused — resume it anytime from the Study page (kept 7 days).");
      setQuestions([]); setStage("setup");
    } catch { toast.error("Could not save the exam."); }
  };

  const resumeExam = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get("/exam/saved/resume", { params: { track: t } });
      setMode("exam"); setTrack(t); setRetry(null); setExamReport(null);
      setQuestions(data.questions); setExamAnswers(data.answers || {}); setIdx(Math.min(data.idx || 0, data.questions.length - 1));
      setExamTitle(data.title); setExamFormat(data.format); setTotalSeconds(data.total_seconds || data.time_limit_seconds);
      setTimeLeft(data.time_limit_seconds); setStage("active");
    } catch { toast.error("Could not resume that exam."); }
    finally { setLoading(false); }
  };

  const discardExam = async (t) => {
    try { await api.delete("/exam/saved", { params: { track: t } }); setSavedExams((s) => s.filter((x) => x.track !== t)); toast.success("Saved exam discarded."); }
    catch { toast.error("Could not discard."); }
  };

  const loadMissed = () => api.get("/study/missed", { params: { limit: 1 } }).then(({ data }) => setMissed({ count: data.count, by_track: data.by_track })).catch(() => {});
  useEffect(() => { if (user) loadMissed(); }, [user, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const beginPractice = (qs, retryInfo) => {
    setMode("practice"); setQuestions(qs); setIdx(0); setSelected(null); setResult(null); setFlipped(false);
    setScore({ correct: 0, answered: 0 }); setRetry(retryInfo); setStage("active");
  };

  const startRetry = async (ids, round = 1) => {
    if (!ids.length) return;
    setLoading(true);
    try {
      const { data } = await api.get("/study/missed", { params: { ids: ids.join(",") } });
      if (!data.questions.length) { toast.error("Those questions are no longer available."); return; }
      beginPractice(data.questions, { wrong: [], round, total: ids.length });
    } catch { toast.error("Could not load missed questions."); }
    finally { setLoading(false); }
  };

  const startMissedDrill = async (t = null) => {
    setLoading(true);
    try {
      const { data } = await api.get("/study/missed", { params: { limit: 50, ...(t ? { track: t } : {}) } });
      if (!data.questions.length) { toast.info("No missed questions yet — nice work."); return; }
      beginPractice(data.questions, { wrong: [], round: 1, total: data.questions.length });
    } catch { toast.error("Could not load missed questions."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/questions/categories").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/bookmarks").then(({ data }) => setBookmarks(data.question_ids || [])).catch(() => {});
    api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/usage").then(({ data }) => setTrial(data?.interviews || null)).catch(() => {});
    loadAllowance();
  }, []);
  const [trial, setTrial] = useState(null); // Free Trial state for the one-time Mini Oral Board (interviews.trial_available / trial_used)

  // exam timer
  useEffect(() => {
    if (stage !== "active" || mode !== "exam" || timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft((s) => {
      if (s <= 1) { clearInterval(t); submitExam(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [stage, mode, timeLeft]);

  const toggleBookmark = async (id) => {
    try {
      const { data } = await api.post("/bookmarks/toggle", { question_id: id });
      setBookmarks((b) => (data.bookmarked ? [...b, id] : b.filter((x) => x !== id)));
    } catch { toast.error("Could not update bookmark."); }
  };

  const startWeakDrill = async () => {
    setWeakLoading(true);
    try {
      const { data } = await api.get("/study/weak-drill", { params: { limit: 10 } });
      if (!data.ready) { toast.info(data.message); return; }
      setMode("practice"); setTrack("Weak-Area Drill");
      setQuestions(data.questions);
      setIdx(0); setSelected(null); setResult(null); setFlipped(false);
      setScore({ correct: 0, answered: 0 }); setStage("active");
      toast.success(`Drilling your weakest areas: ${data.categories.map((c) => c.category).join(", ")}`);
    } catch (e) { if (e.response?.status === 403) { setLimitOpen(true); return; } toast.error("Could not build your drill."); }
    finally { setWeakLoading(false); }
  };

  const configure = (t) => { setTrack(t); setCategory([]); setLevel(null); setStage("configure"); };
  const toggleSection = (s) => setCategory((c) => (c.includes(s) ? c.filter((x) => x !== s) : [...c, s]));

  const start = async (t, opts = {}) => {
    setLoading(true); setTrack(t); setRetry(null);
    try {
      if (mode === "exam") {
        const params = { track: t };
        if (opts.length) params.length = opts.length; else params.count = Math.min(opts.count || 25, leftFor(t));
        if (level) params.level = level;
        const { data } = await api.get("/exam/generate", { params });
        if (!data.questions.length) { toast.error("No questions available."); return; }
        if (data.allowance) setAllowance(data.allowance);
        setQuestions(data.questions); setExamAnswers({}); setIdx(0); setExamTitle(data.title || "Timed Exam");
        setExamFormat(data.format || "custom"); setTotalSeconds(data.time_limit_seconds || data.questions.length * 60);
        setTimeLeft(data.time_limit_seconds || data.questions.length * 60); setExamReport(null); setStage("active");
      } else {
        const cap = leftFor(t);
        const params = { track: t, limit: Math.min(opts.limit || setSize, cap), mode };
        if (opts.category?.length) params.categories = opts.category.join("|");
        if (level) params.level = level;
        const { data } = await api.get("/questions", { params });
        if (!data.questions.length) { toast.error("No questions available in that section yet."); return; }
        if (data.allowance) setAllowance(data.allowance);
        seenCards.current = new Set();
        setQuestions(data.questions);
        setIdx(0); setSelected(null); setResult(null); setFlipped(false);
        setScore({ correct: 0, answered: 0 }); setStage("active");
      }
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (e.response?.status === 403 && code === "daily_limit_reached") { openLimit("daily"); return; }
      if (e.response?.status === 403 && code === "flashcard_limit_reached") { openLimit("flashcards"); return; }
      if (e.response?.status === 403 && e.response?.data?.detail === "premium_required") { toast.info("Full-length exams are a Premium feature."); navigate("/pricing"); return; }
      toast.error("Could not load questions.");
    }
    finally { setLoading(false); }
  };

  const submitAnswer = async (opt) => {
    if (result) return;
    setSelected(opt);
    try {
      const { data } = await api.post("/quiz/submit", { question_id: questions[idx].id, selected: opt });
      setResult(data);
      if (retry && !data.correct) setRetry((r) => ({ ...r, wrong: [...r.wrong, questions[idx].id] }));
      setScore((s) => ({ correct: s.correct + (data.correct ? 1 : 0), answered: s.answered + 1 }));
      if (data.remaining !== null && data.remaining !== undefined && questions[idx].track) {
        const t = questions[idx].track;
        setAllowance((a) => (a ? { ...a, study: { ...a.study, [t]: { ...(a.study?.[t] || {}), remaining: data.remaining } } } : a));
      }
    } catch (e) {
      setSelected(null);
      if (e.response?.status === 403 && e.response?.data?.detail?.code === "daily_limit_reached") { openLimit("daily"); return; }
      toast.error("Could not submit answer.");
    }
  };

  const [starredOnly, setStarredOnly] = useState(false);
  const [fullDeck, setFullDeck] = useState(null);
  const shuffleDeck = () => { const a = [...questions]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } setQuestions(a); setIdx(0); setFlipped(false); toast.info("Deck shuffled."); };
  const toggleStarredOnly = () => {
    if (starredOnly) { setQuestions(fullDeck || questions); setFullDeck(null); setStarredOnly(false); setIdx(0); setFlipped(false); return; }
    const starred = questions.filter((x) => bookmarks.includes(x.id));
    if (!starred.length) return;
    setFullDeck(questions); setQuestions(starred); setStarredOnly(true); setIdx(0); setFlipped(false);
  };
  const prev = () => { if (idx > 0) { setIdx(idx - 1); setFlipped(false); } };
  prevRef.current = prev;
  const next = () => {
    if (idx + 1 < questions.length) { setIdx(idx + 1); setSelected(null); setResult(null); setFlipped(false); return; }
    if (retry) {
      if (retry.wrong.length) { toast.info(`${retry.wrong.length} still missed — going again.`); startRetry(retry.wrong, retry.round + 1); }
      else { toast.success("All cleared — every missed question answered correctly!"); setRetry(null); setStage("setup"); }
      return;
    }
    toast.success(mode === "flashcards" ? "Deck complete!" : `Done — ${score.correct}/${score.answered} correct!`); setStarredOnly(false); setFullDeck(null); setStage("setup");
  };
  nextRef.current = next;

  const submitExam = async () => {
    const answers = questions.map((q) => ({ question_id: q.id, selected: examAnswers[q.id] || null }));
    try {
      const { data } = await api.post("/exam/submit", { answers, track });
      setExamReport(data); setStage("exam-result");
      setSavedExams((s) => s.filter((x) => x.track !== track));
      if (isFree) loadAllowance();
    } catch { toast.error("Could not submit exam."); }
  };

  const fmt = (s) => `${Math.floor(s / 3600) > 0 ? `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}` : Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const showQuota = usage && !usage.is_premium && !user?.is_premium && usage.remaining != null;

  const LimitDialog = (
    <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
      <DialogContent className="max-w-md text-center" data-testid="free-limit-dialog">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mx-auto mb-2">
          <Lock className="w-7 h-7 text-amber-500" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-slate-900">{limitKind === "flashcards" ? "You've used your 40 free flashcards" : "You've used today's free questions"}</h3>
        <p className="text-slate-500" data-testid="free-limit-message">{limitKind === "flashcards"
          ? "The Free plan includes 40 flashcards in total. Premium gives continued, full flashcard access across every track, plus the complete question bank with no daily limits."
          : `Free members get ${track && allowance?.study?.[track] ? allowance.study[track].limit : "50"} study items a day in ${track || "each track"}${allowance?.resets_at ? ` — yours reset ${new Date(allowance.resets_at).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}` : " — they reset at midnight"}. Premium removes the daily limits and opens full timed exams, weak-area drills and advanced progress tracking. Everything you've answered so far stays with your account.`}</p>
        <Button onClick={() => navigate("/pricing")} className="w-full mt-2 bg-primary hover:bg-primary/90 h-12" data-testid="limit-upgrade-button">
          <Flame className="w-4 h-4 mr-2" /> Continue with Premium — <MonthlyPrice className="ml-1" />
        </Button>
        <p className="text-xs text-slate-400">or $200/year · cancel anytime</p>
      </DialogContent>
    </Dialog>
  );

  // ---------- SETUP ----------
  if (stage === "setup") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        {LimitDialog}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-2">{view === "dashboard" ? "Dashboard" : "Study"}</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900" data-testid="dashboard-title">{view === "dashboard" ? `${dash.stats?.total_questions_answered || dash.interviews?.length ? "Welcome back" : "Welcome"}, ${(user?.name || "Candidate").split(" ")[0]}.` : "Study"}</h1>
              {view === "study" && <p className="text-slate-500 mt-2">Practice, flashcards and timed exams across all three tracks.</p>}
            </div>
            {view === "study" && <button onClick={() => navigate("/bookmarks")} className="text-sm text-slate-600 hover:text-primary inline-flex items-center gap-1.5" data-testid="view-bookmarks-button">
              <Bookmark className="w-4 h-4" /> Bookmarks ({bookmarks.length})
            </button>}
          </div>


          {showQuota && (
            <div className="mb-6 flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3" data-testid="quota-banner">
              <p className="text-sm text-amber-800"><span className="font-bold">{usage.remaining}</span> of {usage.limit} free questions remaining. Upgrade for unlimited.</p>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100" onClick={() => navigate("/pricing")} data-testid="quota-upgrade-button">Go Premium</Button>
            </div>
          )}

          {view === "study" && (<>
          {/* ===== PRACTICE QUESTIONS ===== */}
          <section data-testid="practice-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><BookOpen className="w-5 h-5 text-primary" /></div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">Practice Questions</h2>
                  <p className="text-sm text-slate-500">NREMT/EMT · Firefighter I & II · Promotional/Captain</p>
                </div>
              </div>
              {dash.stats && (
                <div className="flex gap-6 text-right" data-testid="practice-stats">
                  <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Answered</p><p className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-questions">{dash.stats.total_questions_answered}</p></div>
                  <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Accuracy</p><p className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-accuracy">{dash.stats.quiz_accuracy}%</p></div>
                </div>
              )}
            </div>
                      {/* Continue improving — compact, personalized */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="continue-improving">
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="weak-drill-card">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Zap className="w-4 h-4 text-primary" /></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-bold text-slate-900">Weak-Area Drill{!user?.is_premium && <Lock className="inline w-3 h-3 ml-1 text-slate-400" />}</p><p className="text-xs text-slate-500 truncate">A set built from the categories you score lowest in.</p></div>
              <Button size="sm" onClick={startWeakDrill} disabled={weakLoading} className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid="weak-drill-button">{weakLoading ? "Building…" : "Start"}</Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="missed-card">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><RotateCcw className="w-4 h-4 text-primary" /></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-bold text-slate-900">Missed Questions <span className="text-primary" data-testid="missed-count">{missed.count}</span></p><p className="text-xs text-slate-500 truncate">{missed.count ? "Drill the ones you got wrong until they stick." : "Nothing missed yet — keep practicing."}</p></div>
              <Button size="sm" onClick={() => startMissedDrill()} disabled={loading || missed.count === 0} variant="outline" className="shrink-0" data-testid="missed-drill-button">Review</Button>
            </div>
          <div className="bg-slate-950 text-white rounded-xl px-4 py-3 flex items-center gap-3" data-testid="dept-research-tile">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Building2 className="w-4 h-4 text-amber-400" /></div>
            <div className="flex-1 min-w-0"><p className="text-sm font-bold">Department Research</p><p className="text-xs text-slate-300 truncate">Learn and drill the departments you're targeting.</p></div>
            <Button size="sm" onClick={() => navigate("/study/departments")} className="bg-white text-slate-950 hover:bg-slate-100 shrink-0" data-testid="dept-research-tile-button">Open</Button>
          </div>
          </div>

          {savedExams.length > 0 && (
            <div className="mb-8 space-y-3" data-testid="saved-exams">
              {savedExams.map((s) => (
                <motion.div key={s.track} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border-2 border-amber-300 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`saved-exam-${s.track.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center"><Pause className="w-6 h-6 text-amber-600" /></div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Paused exam</p>
                      <h3 className="font-heading text-xl text-slate-900">{s.title} · {s.track}</h3>
                      <p className="text-sm text-slate-500">{s.answered} of {s.total} answered · {fmt(s.time_left_seconds)} left · saved {new Date(s.saved_at).toLocaleString()} · expires {new Date(s.expires_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => discardExam(s.track)} className="text-slate-500" data-testid="discard-exam-button">Discard</Button>
                    <Button onClick={() => resumeExam(s.track)} disabled={loading} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold" data-testid="resume-exam-button"><Play className="w-4 h-4 mr-2" /> Resume</Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Mode selector */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {MODES.map((m) => (
              <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
                className={`text-left p-5 rounded-xl border-2 transition-all ${mode === m.id ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-primary/40"}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${mode === m.id ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                    <m.icon className="w-5 h-5" />
                  </div>
                  <span className="font-heading text-lg font-semibold text-slate-900">{m.label}</span>
                </div>
                <p className="mt-2 text-sm text-slate-500">{m.desc}</p>
              </button>
            ))}
          </div>

          <p className="text-sm font-semibold text-slate-600 mb-3">Then pick a track</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {meta.tracks.map((t) => (
              <motion.button key={t} whileHover={{ y: -4 }} onClick={() => configure(t)} disabled={loading}
                data-testid={`study-track-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className="text-left bg-white border border-slate-200 rounded-xl p-5 sm:p-7 hover:border-primary/50 hover:shadow-lg transition-all disabled:opacity-50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4"><Flame className="w-6 h-6 text-primary" /></div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">{t}</h3>
                <p className="mt-1 text-xs text-slate-400">{Object.values(meta.counts?.[t] || {}).reduce((a, b) => a + b, 0)} questions · {(meta.by_track?.[t] || []).length} sections</p>
                {isFree && <p className={`mt-1 text-xs flex items-center gap-1 ${mode === "flashcards" ? (flashLeft > 0 ? "text-slate-500" : "text-amber-700") : (leftFor(t) > 0 ? "text-slate-500" : "text-amber-700")}`} data-testid={`study-track-left-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>{`${leftFor(t)} of ${allowance.study?.[t]?.limit ?? 0} ${mode === "flashcards" ? "flashcards" : "questions"} left today`}</p>}
                <p className="mt-2 text-sm text-slate-500 flex items-center gap-1">Set up {MODES.find((m) => m.id === mode).label.toLowerCase()} <ArrowRight className="w-4 h-4" /></p>
              </motion.button>
            ))}
          </div>

          {/* Department research */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-10 mb-10 bg-slate-950 text-white rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="department-research-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center"><Building2 className="w-6 h-6 text-amber-400" /></div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400">Study track</p><h3 className="font-heading text-2xl text-white">Department Research</h3>
                <p className="text-slate-300 text-sm">Pick a department, learn its history, structure, services, community and hiring process from sourced profiles — then drill it with flashcards and quizzes before station visits and boards.</p>
              </div>
            </div>
            <Button onClick={() => navigate("/study/departments")} className="shrink-0 bg-white text-slate-950 hover:bg-slate-100" data-testid="department-research-button">Open Department Research <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </motion.div>

          <PracticeProgress stats={dash.stats} examHistory={dash.examHistory} />
          </section>
          </>)}

          {view === "dashboard" && (<>
          <DashboardHub roadmap={<RoadMap />} isPremium={!!user?.is_premium} />

          {/* Secondary: weekly goal / streak + free demo reminder */}
          <div className="mt-8 space-y-4" data-testid="dashboard-secondary">
            <StreakCard compact />
            {!user?.is_premium ? (
              <button type="button" onClick={() => navigate("/interview")} className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-primary/50 transition-colors flex items-center gap-4" data-testid="demo-board-cta">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Mic className="w-5 h-5 text-primary" /></div>
                <div>{trial?.trial_used
                  ? <><p className="text-sm font-bold text-slate-900"><span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mr-2">Free Board Used</span>Your one free Mock Oral Board has been used</p><p className="text-xs text-slate-500 mt-0.5">Premium unlocks full graded oral boards — or earn a typed board with a 7-day study streak.</p></>
                  : <><p className="text-sm font-bold text-slate-900"><span className="text-[11px] font-bold uppercase tracking-widest text-amber-500 mr-2">Free</span>Your first Mock Oral Board is free</p><p className="text-xs text-slate-500 mt-0.5">One full general board with the three-person panel — the panel speaks, you answer by voice, and get a quick scorecard.</p></>}</div>
                <ArrowRight className="w-4 h-4 text-slate-400 ml-auto shrink-0" />
              </button>
            ) : null}
          </div>

          {/* ===== DETAILED PROGRESS (collapsed by default) ===== */}
          <section className="mt-14" data-testid="detailed-progress-section">
            <button type="button" onClick={() => setShowDetails((v) => !v)} className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-4 text-left hover:border-slate-300" data-testid="toggle-detailed-progress">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-primary" /></div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">Detailed progress</h2>
                  <p className="text-sm text-slate-500">Study performance, exam history, board replays, recent activity and milestones</p>
                </div>
              </div>
              <span className="text-sm font-semibold text-slate-600 inline-flex items-center gap-1">{showDetails ? "Hide" : "Show"} <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? "rotate-180" : ""}`} /></span>
            </button>
            {showDetails && (<>
            <div className="mt-8" data-testid="practice-progress-section">
              <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><BookOpen className="w-5 h-5 text-primary" /></div>
                  <div>
                    <h3 className="font-heading text-2xl font-semibold text-slate-900">Practice Progress</h3>
                    <p className="text-sm text-slate-500">Where you're strong, where you're slipping</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {dash.stats && (
                    <div className="flex gap-6 text-right" data-testid="practice-stats">
                      <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Answered</p><p className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-questions">{dash.stats.total_questions_answered}</p></div>
                      <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Accuracy</p><p className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-accuracy">{dash.stats.quiz_accuracy}%</p></div>
                      <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Missed</p><p className="font-heading text-2xl font-bold text-amber-600" data-testid="stat-missed">{missed.count}</p></div>
                    </div>
                  )}
                  <Button onClick={() => navigate("/study")} variant="outline" data-testid="dashboard-study-button">Go study <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </div>
              </div>
              <PracticeProgress stats={dash.stats} examHistory={dash.examHistory} defaultOpen />
            </div>

            <div className="mt-12" data-testid="oral-board-section">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center"><Mic className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-slate-900">Oral Board Replays</h3>
                  <p className="text-sm text-slate-500">{dash.stats?.interviews_completed ?? 0} boards · average {dash.stats?.avg_interview_score ?? 0}/100</p>
                </div>
                <Button onClick={() => navigate("/interview")} variant="outline" className="ml-auto" data-testid="dashboard-interview-button"><Mic className="w-4 h-4 mr-2" /> {user?.is_premium ? "Start Oral Board" : trial?.trial_used ? "Mock Oral Board" : "Start Free Trial Board"}</Button>
              </div>
              <BoardProgress interviews={dash.interviews} />
            </div>
            <DashboardExtras />
            </>)}
          </section>
          </>)}

          {view === "study" && !user?.is_premium && (
            <div className="mt-10 bg-slate-950 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
              <div className="flex items-center gap-4">
                <Lock className="w-8 h-8 text-amber-400" />
                <div>
                  <h3 className="font-heading text-xl">Continue your preparation with Premium</h3>
                  <p className="text-slate-400 text-sm">Free members get 50 study items a day in each track, shared across flashcards, practice and timed exams. Premium removes the daily limits and unlocks full timed exams, weak-area drills and advanced analytics.</p>
                </div>
              </div>
              <Button onClick={() => navigate("/pricing")} className="bg-primary hover:bg-primary/90" data-testid="study-upgrade-button">Upgrade to Premium</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- CONFIGURE (sections / exam length) ----------
  if (stage === "configure" && track) {
    const sections = meta.by_track?.[track] || [];
    const counts = meta.counts?.[track] || {};
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    const formats = meta.formats?.[track] || [];
    const isPro = !!user?.is_premium;
    const slug = (s) => s.replace(/[^a-z0-9]/gi, "-").toLowerCase();
    const left = mode === "flashcards" ? flashLeft : leftFor(track);
    const sizeOptions = isFree ? [...new Set([5, 10, 25, 50].filter((n) => n <= left).concat(left > 0 && left < 50 ? [left] : []))].sort((a, b) => a - b) : [5, 10, 25, 50];
    const chosen = isFree ? Math.min(setSize, left) : setSize;
    const counterLine = isFree ? (mode === "flashcards" ? `${left} flashcard${left === 1 ? "" : "s"} left today` : `${left} question${left === 1 ? "" : "s"} left today`) : null;
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        {LimitDialog}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Button variant="ghost" onClick={() => setStage("setup")} className="mb-4" data-testid="configure-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Tracks</Button>
          <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-2">{MODES.find((m) => m.id === mode).label}</p>
          <h1 className="font-heading text-4xl font-bold text-slate-900" data-testid="configure-title">{track}</h1>
          <p className="text-slate-500 mt-2 text-sm">{total} questions across {sections.length} sections · {meta.sources?.[track]}</p>
          <p className="mt-1 text-sm h-5" aria-live="polite" data-testid="allowance-counter">{counterLine && <span className={`inline-flex items-center gap-1 font-medium ${left > 0 ? "text-slate-600" : "text-amber-700"}`}><Layers className="w-3.5 h-3.5" /> {counterLine}{left === 0 && (mode === "flashcards" ? " · Free limit reached" : " · resets at midnight")}</span>}</p>

          {false && meta.levels?.[track] && (
            <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="level-toggle">
              <span className="text-sm text-slate-500 mr-1">Level:</span>
              {[null, ...(meta.level_order || Object.keys(meta.levels[track]))].map((l) => (
                <Button key={l || "all"} variant={level === l ? "default" : "outline"} size="sm" onClick={() => setLevel(l)}
                  className={level === l ? "bg-slate-900 hover:bg-slate-800" : ""} data-testid={`level-${l ? l.replace(/[^a-z0-9]/gi, "-").toLowerCase() : "all"}`}>
                  {l || "Firefighter I & II"}{l && <span className="ml-1.5 text-xs opacity-70">{meta.levels[track][l] || 0}</span>}
                </Button>
              ))}
            </div>
          )}

          {mode === "exam" ? (
            <div className="mt-8">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">Choose your exam length</h2>
              <button onClick={() => start(track, { count: 5 })} disabled={loading || (isFree && left < 5)} data-testid="exam-format-quick5"
                className="w-full mb-4 text-left rounded-xl border-2 border-slate-200 bg-white p-5 flex flex-wrap items-center justify-between gap-3 transition-all hover:border-primary/60 hover:shadow-lg disabled:opacity-50">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Zap className="w-5 h-5" /></div>
                  <div><p className="font-heading text-lg font-semibold text-slate-900">Quick 5</p><p className="text-sm text-slate-500">Five timed questions — a real quick check when you only have a few minutes.</p></div>
                </div>
                <p className="font-mono text-sm text-slate-700"><span className="font-bold">5</span> questions · <span className="font-bold">6</span> min</p>
              </button>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formats.map((f) => {
                  const lockedFmt = f.premium && !isPro;
                  return (
                    <button key={f.id} onClick={() => start(track, { length: f.id })} disabled={loading}
                      data-testid={`exam-format-${f.id}`}
                      className={`text-left rounded-xl border-2 p-6 transition-all bg-white hover:border-primary/60 hover:shadow-lg ${f.id === "full" ? "border-primary/40" : "border-slate-200"} disabled:opacity-50`}>
                      <div className="flex items-center justify-between">
                        <span className="font-heading text-lg font-semibold text-slate-900">{f.label}</span>
                        {lockedFmt ? <Badge className="bg-amber-500 hover:bg-amber-500 text-slate-950"><Lock className="w-3 h-3 mr-1" /> Pro</Badge> : f.id === "full" && <Badge className="bg-primary hover:bg-primary">Realistic</Badge>}
                      </div>
                      <p className="mt-3 font-mono text-sm text-slate-700"><span className="font-bold">{f.count}</span> questions · <span className="font-bold">{f.minutes}</span> min</p>
                      <p className="mt-2 text-sm text-slate-500">{f.desc}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <span className="text-sm text-slate-500">Or a custom size:</span>
                {[10, 15, 35, 75].map((n) => (
                  <Button key={n} variant="outline" size="sm" disabled={loading || (isFree && n > left)} onClick={() => start(track, { count: n })} data-testid={`exam-custom-${n}`}>{n} questions</Button>
                ))}
                {isFree && left > 0 && ![5, 10, 15, 35, 75].includes(left) && (
                  <Button variant="outline" size="sm" disabled={loading} onClick={() => start(track, { count: left })} className="border-primary text-primary" data-testid="exam-custom-remaining">All {left} left today</Button>
                )}
              </div>
              {track === "NREMT / EMT" && (
                <p className="mt-6 text-xs text-slate-400">Half and full exams draw questions in the same proportions as the NREMT test plan: Scene Size-Up 15–19%, Primary Assessment 39–43%, Secondary Assessment 5–9%, Treatment & Transport 20–24%, Operations 10–14%.</p>
              )}
            </div>
          ) : (
            <div className="mt-8">
              <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
                <h2 className="font-heading text-2xl font-semibold text-slate-900">Pick your sections</h2>
                <p className="text-sm text-slate-500" data-testid="section-selection-summary">{category.length === 0 ? "All sections selected" : `${category.length} selected · ${category.reduce((a, c) => a + (counts[c] || 0), 0)} questions`}</p>
              </div>
              <p className="text-xs text-slate-400 mb-3">Tap one section, or tap several to mix them. Tap again to remove.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button onClick={() => setCategory([])} data-testid="section-all"
                  className={`text-left rounded-xl border-2 p-4 bg-white transition-all ${category.length === 0 ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40"}`}>
                  <span className="font-heading font-semibold text-slate-900">All sections</span>
                  <span className="block text-xs text-slate-500 mt-1">{total} questions · mixed</span>
                </button>
                {sections.map((s) => {
                  const on = category.includes(s);
                  return (
                  <button key={s} onClick={() => toggleSection(s)} data-testid={`section-${slug(s)}`} aria-pressed={on}
                    className={`text-left rounded-xl border-2 p-4 bg-white transition-all flex items-start gap-3 ${on ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40"}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary text-white" : "border-slate-300"}`}>{on && <CheckCircle2 className="w-4 h-4" />}</span>
                    <span>
                      <span className="font-heading font-semibold text-slate-900">{s}</span>
                      <span className="block text-xs text-slate-500 mt-1">{counts[s] || 0} questions</span>
                    </span>
                  </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <span className="text-sm text-slate-500">How many:</span>
                {sizeOptions.map((n) => (
                  <Button key={n} variant={chosen === n ? "default" : "outline"} size="sm" onClick={() => setSetSize(n)}
                    className={chosen === n ? "bg-slate-900 hover:bg-slate-800" : ""} data-testid={`set-size-${n}`}>{n}{isFree && n === left && ![5, 10, 25, 50].includes(n) ? " (all left)" : ""}</Button>
                ))}
                {isFree && left > 0 && left < 50 && <span className="text-xs text-slate-400" data-testid="size-cap-note">up to {left} available today</span>}
              </div>
              <Button onClick={() => start(track, { category, limit: chosen })} disabled={loading || (isFree && left === 0)} size="lg" className="mt-8 bg-primary hover:bg-primary/90 h-12 px-8" data-testid="start-set-button">
                {loading ? "Loading…" : isFree && left === 0 ? "Daily limit reached" : `Start ${chosen} ${mode === "flashcards" ? "cards" : "questions"}`} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {isFree && left === 0 && <p className="mt-2 text-xs text-amber-700" data-testid="limit-reached-note">{"Your free study items for this track reset at midnight. Premium removes daily limits."} <button type="button" onClick={() => navigate("/pricing")} className="underline font-semibold">See Premium</button></p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- EXAM RESULT ----------
  if (stage === "exam-result" && examReport) {
    const bySection = {};
    examReport.results.forEach((r) => {
      const k = r.category || "Other";
      bySection[k] = bySection[k] || { total: 0, correct: 0 };
      bySection[k].total += 1; if (r.correct) bySection[k].correct += 1;
    });
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className={`rounded-2xl p-8 text-center text-white ${examReport.passed ? "bg-green-600" : "bg-slate-900"}`} data-testid="exam-result-card">
            <Award className="w-12 h-12 mx-auto mb-3" />
            <p className="uppercase tracking-widest text-xs opacity-80">{examReport.passed ? "Passed" : "Keep training"}</p>
            <p className="font-heading text-6xl font-extrabold">{examReport.score}%</p>
            <p className="opacity-90">{examReport.correct} of {examReport.total} correct · pass mark 70%</p>
          </div>
          {Object.keys(bySection).length > 1 && (
            <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="exam-section-breakdown">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Performance by section</h3>
              <div className="space-y-3">
                {Object.entries(bySection).map(([s, v]) => {
                  const pct = Math.round((v.correct / v.total) * 100);
                  return (
                    <div key={s}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-700">{s}</span>
                        <span className={`font-mono font-semibold ${pct >= 70 ? "text-green-600" : "text-red-600"}`}>{v.correct}/{v.total} · {pct}%</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-6 space-y-3">
            {examReport.results.map((r, i) => (
              <div key={r.question_id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`exam-review-${i}`}>
                <div className="flex items-start gap-2">
                  {r.correct ? <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" /> : <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />}
                  <div>
                    <p className="font-medium text-slate-900">{r.question}</p>
                    <p className="text-sm mt-1 text-slate-500">Correct: <span className="font-semibold uppercase">{r.correct_option}</span> — {r.options[r.correct_option]}</p>
                    <p className="text-sm mt-1 text-slate-600">{r.explanation}</p>
                    {r.concept_note && <p className="text-xs mt-1 text-slate-500" data-testid="concept-note">{r.concept_note}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => setStage("setup")} className="w-full mt-6 bg-primary hover:bg-primary/90" data-testid="exam-done-button">
            <RotateCcw className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          {examReport.results.some((r) => !r.correct) && (
            <Button onClick={() => startRetry(examReport.results.filter((r) => !r.correct).map((r) => r.question_id))} disabled={loading}
              variant="outline" className="w-full mt-3 h-12 border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="retry-missed-button">
              <Target className="w-4 h-4 mr-2" /> Retry the {examReport.results.filter((r) => !r.correct).length} I missed — until I get them all right
            </Button>
          )}
        </div>
      </div>
    );
  }

  const q = questions[idx];
  if (!q) return null;

  // ---------- FLASHCARDS ----------
  if (mode === "flashcards") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={() => { setStage("setup"); setStarredOnly(false); setFullDeck(null); }} data-testid="study-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Modes</Button>
            <div className="flex items-center gap-2">
              {isFree && <span className="text-xs text-slate-500 font-medium" data-testid="flashcards-left-counter">{flashLeft} flashcards left today</span>}
              <Badge variant="secondary" className="font-mono">{idx + 1} / {questions.length}</Badge>
            </div>
          </div>
          <Progress value={(idx / questions.length) * 100} className="mb-4 h-2" />
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4 text-xs" data-testid="flashcard-toolbar">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={shuffleDeck} data-testid="flashcard-shuffle"><Shuffle className="w-3.5 h-3.5 mr-1" /> Shuffle</Button>
              <Button size="sm" variant={starredOnly ? "default" : "outline"} className={starredOnly ? "bg-slate-900 hover:bg-slate-800" : ""} onClick={toggleStarredOnly} disabled={!starredOnly && !questions.some((x) => bookmarks.includes(x.id))} data-testid="flashcard-starred-only"><Bookmark className="w-3.5 h-3.5 mr-1" /> {starredOnly ? "Starred only · on" : `Starred only (${questions.filter((x) => bookmarks.includes(x.id)).length})`}</Button>
            </div>
            <span className="text-slate-400 hidden sm:inline">Star tough cards with the bookmark · <kbd className="px-1 rounded border border-slate-300 bg-white text-[10px]">←</kbd> <kbd className="px-1 rounded border border-slate-300 bg-white text-[10px]">→</kbd> to move · <kbd className="px-1 rounded border border-slate-300 bg-white text-[10px]">Space</kbd> to flip</span>
          </div>
          <div key={q.id} style={{ perspective: 1400 }} onClick={() => setFlipped(!flipped)} className="cursor-pointer" data-testid="flashcard" data-flipped={flipped ? "true" : "false"}>
            <motion.div animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.55, ease: [0.45, 0, 0.2, 1] }} style={{ transformStyle: "preserve-3d" }} className="grid">
              <div style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }} className={`col-start-1 row-start-1 bg-white border border-slate-200 rounded-2xl p-6 flex flex-col shadow-sm ${flipped ? "pointer-events-none" : ""}`} aria-hidden={flipped} data-testid="flashcard-front">
                <div className="flex items-center justify-between">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{q.category}</Badge>
                  <span className="relative flex items-center gap-1" onClick={(e) => e.stopPropagation()}><ReportQuestion questionId={q.id} compact /><BookmarkBtn id={q.id} bookmarks={bookmarks} onToggle={toggleBookmark} /></span>
                </div>
                <p className="font-heading text-xl font-semibold text-slate-900 leading-snug mt-4" data-testid="flashcard-question">{q.question}</p>
                <ul className="mt-4 grid gap-1.5 select-none" onClick={(e) => e.stopPropagation()} data-testid="flashcard-choices">
                  {Object.entries(q.options).map(([k, v]) => <li key={k} className="flex gap-2 text-sm text-slate-700 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 cursor-default"><span className="font-bold text-slate-500 w-4 shrink-0">{k.toUpperCase()}</span><span>{v}</span></li>)}
                </ul>
                <p className="mt-4 text-xs text-slate-500 flex items-center justify-center gap-1" data-testid="flashcard-flip-hint"><Eye className="w-4 h-4" /> Decide on your answer, then tap the card or press <kbd className="px-1.5 py-0.5 rounded border border-slate-300 bg-white text-[10px] font-semibold text-slate-700">Space</kbd> to flip</p>
              </div>
              <div style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }} className={`col-start-1 row-start-1 bg-white border-2 border-emerald-200 rounded-2xl p-6 flex flex-col shadow-sm ${flipped ? "" : "pointer-events-none"}`} aria-hidden={!flipped} data-testid="flashcard-back">
                <div className="flex items-center justify-between">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{q.category}</Badge>
                  <span className="relative flex items-center gap-1" onClick={(e) => e.stopPropagation()}><ReportQuestion questionId={q.id} compact /><BookmarkBtn id={q.id} bookmarks={bookmarks} onToggle={toggleBookmark} /></span>
                </div>
                <p className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mt-4">Correct answer</p>
                <p className="font-heading text-xl text-slate-900 mt-1" data-testid="flashcard-answer">{String(q.correct).toUpperCase()} — {q.options[q.correct]}</p>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid="flashcard-explanation">{q.explanation}</p>
                {q.concept_note && <p className="text-xs mt-3 text-slate-500" data-testid="concept-note">{q.concept_note}</p>}
                <div onClick={(e) => e.stopPropagation()}><ReportQuestion questionId={q.id} /></div>
                <p className="mt-3 text-xs text-slate-400 text-center">Tap or press Space to flip back · Next card →</p>
              </div>
            </motion.div>
          </div>
          <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-3 bg-slate-50/95 backdrop-blur border-t border-slate-200/70 flex items-center justify-between gap-3" data-testid="study-nav-bar">
            <Button variant="outline" disabled={idx === 0} onClick={prev} data-testid="flashcard-prev-button"><ArrowLeft className="w-4 h-4 mr-1" /> Prev</Button>
            <Button onClick={next} className="flex-1 bg-primary hover:bg-primary/90" data-testid="flashcard-next-button">
              {idx + 1 < questions.length ? "Next card" : "Finish"} <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- EXAM (active) ----------
  if (mode === "exam") {
    const answered = Object.keys(examAnswers).length;
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-4 sticky top-16 z-20 bg-slate-50/95 backdrop-blur py-2 -mx-2 px-2 rounded-lg" data-testid="exam-header">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="font-mono">{idx + 1} / {questions.length}</Badge>
              <span className="text-sm font-semibold text-slate-700" data-testid="exam-title">{examTitle}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={pauseExam} data-testid="pause-exam-button" title="Pause and save — resume later from the Study page">
                <Pause className="w-4 h-4 mr-1" /> Pause
              </Button>
              <div className={`font-mono font-bold px-3 py-1 rounded-lg ${timeLeft < 60 ? "bg-red-100 text-red-600" : "bg-slate-900 text-white"}`} data-testid="exam-timer">
                <Timer className="w-4 h-4 inline mr-1" />{fmt(timeLeft)}
              </div>
            </div>
          </div>
          <Progress value={(answered / questions.length) * 100} className="mb-6 h-2" />
          <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
            <div className="relative flex items-center justify-between mb-4"><Badge className="bg-primary/10 text-primary hover:bg-primary/10">{q.category}</Badge><ReportQuestion questionId={q.id} compact /></div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 leading-snug">{q.question}</h2>
            <div className="mt-6 space-y-3">
              {Object.entries(q.options).map(([key, text]) => (
                <button key={key} onClick={() => setExamAnswers({ ...examAnswers, [q.id]: key })} data-testid={`exam-option-${key}`}
                  className={`w-full text-left px-5 py-4 rounded-xl border-2 flex items-center gap-3 transition-all
                    ${examAnswers[q.id] === key ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40 bg-white"}`}>
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-heading font-bold uppercase shrink-0 ${examAnswers[q.id] === key ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{key}</span>
                  <span className="text-slate-800">{text}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-3 bg-slate-50/95 backdrop-blur border-t border-slate-200/70 flex items-center justify-between" data-testid="study-nav-bar">
            <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="exam-prev-button"><ArrowLeft className="w-4 h-4 mr-1" /> Prev</Button>
            {idx + 1 < questions.length ? (
              <Button onClick={() => setIdx(idx + 1)} className="bg-slate-900 hover:bg-slate-800" data-testid="exam-next-button">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button onClick={submitExam} className="bg-primary hover:bg-primary/90" data-testid="exam-submit-button">Submit Exam ({answered}/{questions.length})</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- PRACTICE ----------
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      {LimitDialog}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => { setRetry(null); setStage("setup"); }} data-testid="study-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Modes</Button>
          <div className="flex items-center gap-2">
            {retry && <Badge className="bg-amber-500 text-slate-950 hover:bg-amber-500" data-testid="retry-round-badge">Missed drill · Round {retry.round}</Badge>}
            {isFree && q.track && allowance.study?.[q.track] && <span className="text-xs text-slate-500 font-medium" data-testid="questions-left-counter">{leftFor(q.track)} left today</span>}
            <Badge variant="secondary" className="font-mono">{idx + 1} / {questions.length}</Badge>
          </div>
        </div>
        <Progress value={(idx / questions.length) * 100} className="mb-6 h-2" />

        <motion.div key={q.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{q.category}</Badge>
              {false && q.level && <Badge variant="outline" className="text-slate-600">{q.level}</Badge>}
              {q.premium && <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/15">Premium</Badge>}
            </div>
            <span className="relative flex items-center gap-1"><ReportQuestion questionId={q.id} compact /><BookmarkBtn id={q.id} bookmarks={bookmarks} onToggle={toggleBookmark} /></span>
          </div>
          <h2 className="font-heading text-2xl font-semibold text-slate-900 leading-snug">{q.question}</h2>

          <div className="mt-6 space-y-3">
            {Object.entries(q.options).map(([key, text]) => {
              const isSelected = selected === key;
              const isCorrect = result && key === result.correct_option;
              const isWrong = result && isSelected && !result.correct;
              return (
                <button key={key} onClick={() => submitAnswer(key)} disabled={!!result} data-testid={`quiz-option-${key}`}
                  className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all flex items-center gap-3
                    ${isCorrect ? "border-green-500 bg-green-50" : isWrong ? "border-red-500 bg-red-50" : isSelected ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40 bg-white"}
                    ${result ? "cursor-default" : "cursor-pointer"}`}>
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-heading font-bold uppercase shrink-0 ${isCorrect ? "bg-green-500 text-white" : isWrong ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600"}`}>{key}</span>
                  <span className="text-slate-800">{text}</span>
                  {isCorrect && <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />}
                  {isWrong && <XCircle className="w-5 h-5 text-red-500 ml-auto" />}
                </button>
              );
            })}
          </div>

          {result && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 p-5 rounded-xl bg-slate-50 border border-slate-200">
              <p className={`font-heading text-lg ${result.correct ? "text-green-600" : "text-red-600"}`}>{result.correct ? "Correct!" : "Not quite."}</p>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{result.explanation}</p>
              {q.concept_note && <p className="text-xs mt-2 text-slate-500" data-testid="concept-note">{q.concept_note}</p>}
            </motion.div>
          )}
        </motion.div>

        {/* Stable navigation: always the same bar under the card, pinned to the bottom edge on long questions */}
        <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-3 bg-slate-50/95 backdrop-blur border-t border-slate-200/70 flex items-center justify-between gap-3" data-testid="study-nav-bar">
          <span className="flex items-center gap-2 text-sm text-slate-500"><Flame className="w-4 h-4 text-primary" /> Score: {score.correct}/{score.answered}</span>
          <Button onClick={next} disabled={!result} className="bg-primary hover:bg-primary/90 disabled:opacity-40 min-w-[180px]" data-testid="quiz-next-button">
            {idx + 1 < questions.length ? "Next question" : retry ? (retry.wrong.length ? `Retry ${retry.wrong.length} still missed` : "Finish — all correct!") : "Finish"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
